import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

interface UserPreferences {
  weight_unit: 'kg' | 'lbs';
  distance_unit: 'km' | 'miles';
  temperature_unit: 'celsius' | 'fahrenheit';
  time_format: 'hh:mm:ss' | 'mm:ss' | 'decimal_minutes';
  pace_format: 'min_per_km' | 'min_per_mile' | 'km_per_hour' | 'miles_per_hour';
  date_format: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  chart_theme: 'light' | 'dark' | 'auto';
  notifications_enabled: boolean;
  goal_reminders: boolean;
  milestone_celebrations: boolean;
  weekly_reports: boolean;
}

const defaultPreferences: UserPreferences = {
  weight_unit: 'kg',
  distance_unit: 'km',
  temperature_unit: 'celsius',
  time_format: 'mm:ss',
  pace_format: 'min_per_km',
  date_format: 'DD/MM/YYYY',
  chart_theme: 'light',
  notifications_enabled: true,
  goal_reminders: true,
  milestone_celebrations: true,
  weekly_reports: false,
};

export const useMeasurementPreferences = () => {
  const { user } = useAuth();

  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['user-measurement-preferences'],
    queryFn: async () => {
      if (!user) return defaultPreferences;
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user preferences:', error);
        return defaultPreferences;
      }
      
      if (!data) return defaultPreferences;
      
      return {
        weight_unit: data.weight_unit as 'kg' | 'lbs',
        distance_unit: data.distance_unit as 'km' | 'miles',
        temperature_unit: data.temperature_unit as 'celsius' | 'fahrenheit',
        time_format: data.time_format as 'hh:mm:ss' | 'mm:ss' | 'decimal_minutes',
        pace_format: data.pace_format as 'min_per_km' | 'min_per_mile' | 'km_per_hour' | 'miles_per_hour',
        date_format: data.date_format as 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD',
        chart_theme: data.chart_theme as 'light' | 'dark' | 'auto',
        notifications_enabled: data.notifications_enabled,
        goal_reminders: data.goal_reminders,
        milestone_celebrations: data.milestone_celebrations,
        weekly_reports: data.weekly_reports,
      };
    },
    enabled: !!user,
  });

  return {
    preferences: preferences || defaultPreferences,
    isLoading,
    error,
  };
};

// Conversion utility functions
export const convertWeight = (value: number, from: 'kg' | 'lbs', to: 'kg' | 'lbs'): number => {
  if (from === to) return value;
  if (from === 'kg' && to === 'lbs') return value * 2.20462;
  if (from === 'lbs' && to === 'kg') return value / 2.20462;
  return value;
};

export const convertDistance = (value: number, from: 'km' | 'miles', to: 'km' | 'miles'): number => {
  if (from === to) return value;
  if (from === 'km' && to === 'miles') return value / 1.60934;
  if (from === 'miles' && to === 'km') return value * 1.60934;
  return value;
};

export const convertTemperature = (value: number, from: 'celsius' | 'fahrenheit', to: 'celsius' | 'fahrenheit'): number => {
  if (from === to) return value;
  if (from === 'celsius' && to === 'fahrenheit') return (value * 9/5) + 32;
  if (from === 'fahrenheit' && to === 'celsius') return (value - 32) * 5/9;
  return value;
};